import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import usePageTitle from '../../hooks/usePageTitle';
import { useAuth } from '../../AuthContext';
import { useAxios } from '../../core/_axios';
import BaseModal from '../../components/_BaseModal';
import AiModelsSettings from './AiModelsSettings';
import { usePageHeader } from '../../contexts/PageHeaderContext';

interface SystemSetting {
  key: string;
  value: any;
  raw_value: string;
  description: string;
  value_type: string;
  is_sensitive: boolean;
  updated_by: string | null;
  updated_at: string;
}

interface EmailAISettings {
  quality_model: string;
  economical_model: string;
}

interface SystemSettingRevision {
  id: number;
  revision_number: number;
  value: string;
  previous_value: string | null;
  changed_by: string;
  change_description: string | null;
  inserted_at: string;
}

type TabType = 'ai-models' | 'ai-prompts';
type PromptTabType = 'email-generation' | 'email-edit-analysis' | 'signature-transcription';

const SystemSettings: React.FC = () => {
  const { copy } = useLocalizedCopy();
  const { currentUser } = useAuth();
  const axios = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageHeader } = usePageHeader();
  const { tab, promptType } = useParams<{ tab?: string; promptType?: string }>();

  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [emailAISettings, setEmailAISettings] = useState<EmailAISettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [viewingRevisions, setViewingRevisions] = useState<SystemSetting | null>(null);
  const [revisions, setRevisions] = useState<SystemSettingRevision[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [changeDescription, setChangeDescription] = useState('');

  // Derive active tabs from URL
  const activeTab = (tab as TabType) || 'ai-models';
  const activePromptTab = (promptType as PromptTabType) || 'email-generation';

  // Use refs for edit values to avoid re-render issues
  const editValueRef = useRef<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>(null);
  const changeDescriptionRef = useRef<HTMLInputElement>(null);

  usePageTitle('System Settings');

  // Create navigation items
  const navigationItems = useMemo(() => {
    const isAiModels = location.pathname.includes('/ai-models');
    return (
      <div className="flex gap-4 text-sm">
        <Link 
          to="/admin/system-settings/ai-models" 
          className={isAiModels ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
        >
          AI Models
        </Link>
        <Link 
          to="/admin/system-settings/ai-prompts/email-generation"
          className={!isAiModels ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
        >
          AI Prompts
        </Link>
      </div>
    );
  }, [location.pathname]);

  // Set up page header with navigation
  useEffect(() => {
    setPageHeader('System Settings', navigationItems);
    return () => setPageHeader(null);
  }, [setPageHeader, navigationItems]);

  useEffect(() => {
    if (currentUser?.is_super_super_user) {
      fetchSettings();
      fetchEmailAISettings();
    }
  }, [currentUser]);

  // Redirect to default tabs if at base route
  useEffect(() => {
    if (!tab) {
      navigate('/admin/system-settings/ai-models', { replace: true });
    } else if (tab === 'ai-prompts' && !promptType) {
      navigate('/admin/system-settings/ai-prompts/email-generation', { replace: true });
    }
  }, [tab, promptType, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/v1/app/authenticated/admin/system-settings');
      setSettings(response.data.system_settings);
    } catch (error) {
      console.error('Failed to load system settings');
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailAISettings = async () => {
    try {
      const response = await axios.get('/api/v1/app/authenticated/admin/system-settings/email-ai');
      setEmailAISettings(response.data);
    } catch (error) {
      console.error('Error fetching email AI settings:', error);
    }
  };

  const handleEdit = (setting: SystemSetting) => {
    setEditingSetting(setting);
    setChangeDescription('');
  };

  const fetchRevisions = async (settingKey: string) => {
    setLoadingRevisions(true);
    try {
      const response = await axios.get(`/api/v1/app/authenticated/admin/system-settings/${settingKey}/revisions`);
      setRevisions(response.data.revisions);
    } catch (error) {
      console.error('Failed to load revision history:', error);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleViewHistory = async (setting: SystemSetting) => {
    setViewingRevisions(setting);
    await fetchRevisions(setting.key);
  };

  const handleSave = async () => {
    if (!editingSetting || !editValueRef.current) return;

    const newValue = editValueRef.current.value;
    const description = changeDescriptionRef.current?.value || 'Manual update via system settings';

    setSavingKey(editingSetting.key);
    try {
      const response = await axios.put(`/api/v1/app/authenticated/admin/system-settings/${editingSetting.key}`, {
        value: newValue,
        change_description: description
      });

      // Update local state
      setSettings(settings.map(s =>
        s.key === editingSetting.key ? response.data.system_setting : s
      ));

      // Refresh email AI settings if we updated an email-related setting
      if (editingSetting.key.startsWith('email_ai_')) {
        await fetchEmailAISettings();
      }

      console.log('Setting updated successfully');
      setEditingSetting(null);
    } catch (error) {
      console.error('Failed to update setting');
      console.error('Error updating setting:', error);
    } finally {
      setSavingKey(null);
    }
  };


  const formatValue = (setting: SystemSetting) => {
    if (setting.is_sensitive) {
      return '••••••••';
    }

    if (setting.value_type === 'json') {
      return <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">{JSON.stringify(setting.value, null, 2)}</pre>;
    }

    if (setting.value_type === 'boolean') {
      return setting.value ? 'True' : 'False';
    }

    if (setting.value_type === 'text' && setting.key.includes('prompt')) {
      const preview = String(setting.value).substring(0, 100);
      return (
        <span className="text-gray-600">
          {preview}...
          <button
            className="text-blue-600 hover:text-blue-800 ml-2"
            onClick={() => handleEdit(setting)}
          >
            View full
          </button>
        </span>
      );
    }

    return String(setting.value);
  };

  const getEditInput = () => {
    if (!editingSetting) return null;

    const defaultValue = editingSetting.raw_value || editingSetting.value;

    // Special handling for model selection
    if (editingSetting.key === 'ai_quality_model' || editingSetting.key === 'ai_economical_model') {
      const availableModels = ['gpt-5', 'gpt-5-mini', 'gpt-5-nano'];
      return (
        <select
          ref={editValueRef as React.RefObject<HTMLSelectElement>}
          className="form-select"
          defaultValue={defaultValue}
        >
          {availableModels.map(model => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>
      );
    }

    // Special handling for prompts
    if (editingSetting.key.includes('prompt')) {
      return (
        <div className="w-full -mx-0.5 px-0.5">
          <div className="relative">
            <textarea
              ref={editValueRef as React.RefObject<HTMLTextAreaElement>}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={15}
              defaultValue={defaultValue}
              style={{ whiteSpace: 'pre-wrap', resize: 'vertical' }}
            />
            <button
              type="button"
              className="absolute top-2 right-2 btn btn-sm btn-light"
              onClick={(e) => {
                if (editValueRef.current) {
                  navigator.clipboard.writeText((editValueRef.current as HTMLTextAreaElement).value);
                  const button = e.currentTarget;
                  const originalHTML = button.innerHTML;
                  button.innerHTML = '<i class="ki-filled ki-check fs-6"></i>';
                  button.disabled = true;
                  setTimeout(() => {
                    button.innerHTML = originalHTML;
                    button.disabled = false;
                  }, 2000);
                }
              }}
              title="Copy current text to clipboard"
            >
              <i className="ki-filled ki-copy fs-6"></i>
            </button>
          </div>
        </div>
      );
    }

    switch (editingSetting.value_type) {
      case 'boolean':
        return (
          <select
            ref={editValueRef as React.RefObject<HTMLSelectElement>}
            className="form-select"
            defaultValue={defaultValue}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );

      case 'integer':
        return (
          <input
            ref={editValueRef as React.RefObject<HTMLInputElement>}
            type="number"
            className="form-control"
            defaultValue={defaultValue}
          />
        );

      case 'float':
        return (
          <input
            ref={editValueRef as React.RefObject<HTMLInputElement>}
            type="number"
            step="0.1"
            className="form-control"
            defaultValue={defaultValue}
          />
        );

      case 'json':
        return (
          <textarea
            ref={editValueRef as React.RefObject<HTMLTextAreaElement>}
            className="form-control font-mono text-sm"
            rows={6}
            defaultValue={defaultValue}
          />
        );

      default:
        return (
          <input
            ref={editValueRef as React.RefObject<HTMLInputElement>}
            type="text"
            className="form-control"
            defaultValue={defaultValue}
          />
        );
    }
  };

  const aiPromptSettings = settings.filter(s => s.key.includes('ai_prompt_'));
  const aiModelSettings = settings.filter(s => (s.key === 'ai_quality_model' || s.key === 'ai_economical_model'));

  const getPromptUsageDescription = (key: string) => {
    switch (key) {
      case 'ai_prompt_email_generation_system':
        return 'Used when generating follow-up emails for funeral attendees. This prompt defines the AI\'s personality and output format.';
      case 'ai_prompt_email_edit_analysis':
        return 'Used when analyzing human edits to AI-generated emails. Helps the system learn from corrections.';
      case 'ai_prompt_signature_transcription':
        return 'Used for extracting handwritten text from guestbook signatures and converting to digital text.';
      default:
        return 'AI prompt configuration';
    }
  };

  const getPromptBackendReferences = (key: string) => {
    switch (key) {
      case 'ai_prompt_email_generation_system':
        return [
          'lib/service_guestbook/email_follow_up/email_generator.ex:68',
          'lib/service_guestbook/system_settings.ex:102-126'
        ];
      case 'ai_prompt_email_edit_analysis':
        return [
          'lib/service_guestbook/email_follow_up/review_service.ex:301',
          'lib/service_guestbook/system_settings.ex:131-145'
        ];
      case 'ai_prompt_signature_transcription':
        return [
          'lib/service_guestbook/openai/supervision/service_signature_watcher.ex:213',
          'lib/service_guestbook/system_settings.ex:150-153'
        ];
      default:
        return [];
    }
  };

  const getPromptTitle = (key: string) => {
    switch (key) {
      case 'ai_prompt_email_generation_system':
        return 'Email Generation System Prompt';
      case 'ai_prompt_email_edit_analysis':
        return 'Email Edit Analysis Prompt';
      case 'ai_prompt_signature_transcription':
        return 'Signature Transcription Prompt';
      default:
        return key.replace('ai_prompt_', '').replace(/_/g, ' ').toUpperCase();
    }
  };

  if (!currentUser?.is_super_super_user) {
    return (
      <div className="alert alert-danger">
        <i className="ki-filled ki-shield-cross fs-2 me-2"></i>
        You do not have permission to access system settings.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-10">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fixed">

      {/* Tab Content */}
      <div>
        {/* AI Models Tab */}
        {activeTab === 'ai-models' && (
          <AiModelsSettings />
        )}

        {/* Old AI Models Tab - Hidden */}
        {false && activeTab === 'ai-models' && emailAISettings && (
          <div>
            <div className="card mb-8">
              <div className="card-header">
                <h3 className="card-title">AI Model Configuration</h3>
                <div className="card-toolbar">
                  <span className="badge badge-light-primary">OpenAI Models</span>
                </div>
              </div>
              <div className="card-body p-8">
                <div className="row g-8">
                  <div className="col-md-6">
                    <div className="border border-dashed border-gray-300 rounded p-6 bg-light-primary">
                      <div className="mb-4">
                        <h4 className="text-gray-900 fw-bold mb-1">Highest Quality Model</h4>
                        <div className="text-gray-600 fs-6 mb-3">Used for first 3 emails & edit analysis</div>
                        <button
                          className="btn btn-light-primary btn-sm"
                          onClick={() => {
                            const setting = settings.find(s => s.key === 'ai_quality_model');
                            if (setting) handleEdit(setting);
                          }}
                          disabled={!settings.find(s => s.key === 'ai_quality_model')}
                        >
                          <span className="fw-bold">{emailAISettings.quality_model}</span>
                          <i className="ki-filled ki-pencil fs-7 ms-2"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="border border-dashed border-gray-300 rounded p-6 bg-light-success">
                      <div className="mb-4">
                        <h4 className="text-gray-900 fw-bold mb-1">Economical and Fast Model</h4>
                        <div className="text-gray-600 fs-6 mb-3">Cost-optimized operations</div>
                        <button
                          className="btn btn-light-success btn-sm"
                          onClick={() => {
                            const setting = settings.find(s => s.key === 'ai_economical_model');
                            if (setting) handleEdit(setting);
                          }}
                          disabled={!settings.find(s => s.key === 'ai_economical_model')}
                        >
                          <span className="fw-bold">{emailAISettings.economical_model}</span>
                          <i className="ki-filled ki-pencil fs-7 ms-2"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Other AI Model Settings */}
                {aiModelSettings.filter(s => s.key !== 'ai_quality_model' && s.key !== 'ai_economical_model').length > 0 && (
                  <div className="mt-8">
                    <h5 className="text-gray-800 mb-4">Additional AI Settings</h5>
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered gy-3 gs-7">
                        <thead>
                          <tr className="fw-semibold fs-6 text-gray-800 border-bottom border-gray-200">
                            <th>Setting</th>
                            <th>Value</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiModelSettings
                            .filter(s => s.key !== 'ai_quality_model' && s.key !== 'ai_economical_model')
                            .map(setting => (
                              <tr key={setting.key}>
                                <td className="fw-semibold">{setting.key.replace('ai_', '')}</td>
                                <td>{formatValue(setting)}</td>
                                <td className="text-gray-600">{setting.description}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => handleEdit(setting)}
                                    disabled={savingKey === setting.key}
                                  >
                                    <i className="ki-filled ki-pencil fs-6"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AI Prompts Tab */}
        {activeTab === 'ai-prompts' && (
          <div>
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">AI Prompt Configuration</h3>
                <div className="card-toolbar">
                  <span className="badge badge-light-info">Customize AI behavior</span>
                </div>
              </div>
              <div className="card-body">
                {aiPromptSettings.length === 0 ? (
                  <div className="text-center py-10">
                    <i className="ki-filled ki-message-programming fs-3x text-gray-400 mb-4"></i>
                    <p className="text-gray-600">No AI prompts configured yet. Run migrations to add prompt settings.</p>
                  </div>
                ) : (
                  <div>
                    {/* Sub-navigation for prompts */}
                    <div className="mb-6">
                      <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200">
                        <li className="me-2">
                          <button
                            onClick={() => navigate('/admin/system-settings/ai-prompts/email-generation')}
                            className={[
                              activePromptTab === 'email-generation'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700',
                              'inline-block px-4 py-3'
                            ].join(' ')}
                          >
                            Email Generation
                          </button>
                        </li>
                        <li className="me-2">
                          <button
                            onClick={() => navigate('/admin/system-settings/ai-prompts/email-edit-analysis')}
                            className={[
                              activePromptTab === 'email-edit-analysis'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700',
                              'inline-block px-4 py-3'
                            ].join(' ')}
                          >
                            Edit Analysis
                          </button>
                        </li>
                        <li className="me-2">
                          <button
                            onClick={() => navigate('/admin/system-settings/ai-prompts/signature-transcription')}
                            className={[
                              activePromptTab === 'signature-transcription'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700',
                              'inline-block px-4 py-3'
                            ].join(' ')}
                          >
                            Signature Transcription
                          </button>
                        </li>
                      </ul>
                    </div>

                    {/* Prompt content based on active sub-tab */}
                    {(() => {
                      let currentPromptKey = '';
                      if (activePromptTab === 'email-generation') {
                        currentPromptKey = 'ai_prompt_email_generation_system';
                      } else if (activePromptTab === 'email-edit-analysis') {
                        currentPromptKey = 'ai_prompt_email_edit_analysis';
                      } else if (activePromptTab === 'signature-transcription') {
                        currentPromptKey = 'ai_prompt_signature_transcription';
                      }

                      const setting = aiPromptSettings.find(s => s.key === currentPromptKey);

                      if (!setting) return null;

                      return (
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{getPromptTitle(setting.key)}</h4>
                            <p className="text-sm text-gray-600">{getPromptUsageDescription(setting.key)}</p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Prompt:</label>
                            <div className="relative">
                              <pre className="bg-gray-50 p-4 rounded-lg text-sm font-mono overflow-auto whitespace-pre-wrap max-h-96">
                                {setting.value}
                              </pre>
                              <button
                                className="absolute top-2 right-2 btn btn-sm btn-light-primary"
                                onClick={(e) => {
                                  navigator.clipboard.writeText(setting.value);
                                  // Show brief success feedback
                                  const button = e.currentTarget;
                                  const originalHTML = button.innerHTML;
                                  button.innerHTML = '<i class="ki-filled ki-check fs-6 me-1"></i>Copied!';
                                  button.disabled = true;
                                  setTimeout(() => {
                                    button.innerHTML = originalHTML;
                                    button.disabled = false;
                                  }, 2000);
                                }}
                                title="Copy to clipboard"
                              >
                                <i className="ki-filled ki-copy fs-6 me-1"></i>
                                Copy
                              </button>
                            </div>
                          </div>

                          {/* Backend References - Only show in development */}
                          {window.location.hostname === 'localhost' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Backend References:</label>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                {getPromptBackendReferences(setting.key).map((ref, index) => (
                                  <div key={index} className="text-sm font-mono text-gray-600">
                                    <span className="text-gray-400">→</span> {ref}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-4 border-t">
                            <div className="text-sm text-gray-500">
                              Last updated: {new Date(setting.updated_at).toLocaleString()}
                              {setting.updated_by && <span className="ml-2">by {setting.updated_by}</span>}
                            </div>
                            <div className="flex gap-2">
                              <button
                                className="btn btn-light"
                                onClick={() => handleViewHistory(setting)}
                              >
                                <i className="ki-filled ki-time fs-6 me-2"></i>
                                View History
                              </button>
                              <button
                                className="btn btn-primary"
                                onClick={() => handleEdit(setting)}
                              >
                                <i className="ki-filled ki-pencil fs-6 me-2"></i>
                                Edit Prompt
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingSetting && (
        <BaseModal
          isOpen={!!editingSetting}
          setIsOpen={(open) => !open && setEditingSetting(null)}
          title={editingSetting.key.includes('prompt') ? getPromptTitle(editingSetting.key) : `Edit: ${editingSetting.key}`}
          actions={
            <>
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={() => setEditingSetting(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={savingKey !== null}
              >
                {savingKey ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </>
          }
        >
          <div className="w-full">
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <p className="text-gray-600">{editingSetting.description}</p>
            </div>

            {!editingSetting.key.includes('prompt') && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Value</label>
                <div className="bg-gray-100 p-3 rounded">{formatValue(editingSetting)}</div>
              </div>
            )}

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {editingSetting.key.includes('prompt') ? 'Edit Prompt' : 'New Value'}
              </label>
              {getEditInput()}
              <div className="text-sm text-gray-500 mt-1">Type: {editingSetting.value_type}</div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Change Description</label>
              <input
                ref={changeDescriptionRef}
                type="text"
                className="form-control"
                placeholder="Describe what you changed and why..."
                defaultValue=""
              />
              <div className="text-sm text-gray-500 mt-1">This helps track why changes were made</div>
            </div>
          </div>
        </BaseModal>
      )}

      {/* Revision History Modal */}
      {viewingRevisions && (
        <BaseModal
          isOpen={!!viewingRevisions}
          setIsOpen={(open) => !open && setViewingRevisions(null)}
          title={`Revision History: ${getPromptTitle(viewingRevisions.key)}`}
          actions={
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setViewingRevisions(null)}
            >
              Close
            </button>
          }
        >
          <div className="w-full">
            {loadingRevisions ? (
              <div className="d-flex justify-content-center p-10">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : revisions.length === 0 ? (
              <div className="text-center py-10">
                <i className="ki-filled ki-time fs-3x text-gray-400 mb-4"></i>
                <p className="text-gray-600">No revision history available yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {revisions.map((revision, index) => (
                  <div key={revision.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="badge badge-light-primary me-2">Revision #{revision.revision_number}</span>
                        <span className="text-sm text-gray-600">
                          {new Date(revision.inserted_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        by {revision.changed_by}
                      </div>
                    </div>

                    {revision.change_description && (
                      <div className="mb-3">
                        <label className="text-sm font-medium text-gray-700">Change Description:</label>
                        <p className="text-sm text-gray-600 mt-1">{revision.change_description}</p>
                      </div>
                    )}

                    {/* Show diff for prompts */}
                    {viewingRevisions.key.includes('prompt') && revision.previous_value && (
                      <div className="mb-3">
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Changes:</label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-gray-500">Previous:</label>
                            <pre className="bg-red-50 p-3 rounded text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                              {revision.previous_value}
                            </pre>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">New:</label>
                            <pre className="bg-green-50 p-3 rounded text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                              {revision.value}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show simple value change for non-prompts */}
                    {!viewingRevisions.key.includes('prompt') && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600">Changed from:</span>
                        <code className="bg-red-100 px-2 py-1 rounded">{revision.previous_value || 'empty'}</code>
                        <span className="text-gray-600">to:</span>
                        <code className="bg-green-100 px-2 py-1 rounded">{revision.value}</code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </BaseModal>
      )}
    </div>
  );
};

export default SystemSettings;